<template>
    <AuthLayout>
        <template v-if="step === 'phone'">
            <p class="login-subtitle" v-lang="'login-title'"></p>

            <form @submit.prevent="sendSMS" class="login-form">
                <div class="input-group" :class="{ 'has-error': errors.length > 0 }">
                    <label>
                        <span v-lang="'login-phoneLabel'"></span>
                        <span class="required-asterisk">*</span>
                    </label>
                    <div class="input-wrapper">
                        <span class="input-icon">
                            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                        </span>
                        <input
                            type="tel"
                            v-model="phone"
                            v-mask="'(##) #####-####'"
                            :placeholder="t('login-phonePlaceholder')"
                            @focus="scrollFormIntoView"
                        />
                    </div>
                    <span class="error-message" v-if="errors.length > 0">{{ errors[0] }}</span>
                </div>

                <button type="submit" class="btn-primary" :disabled="loading" :class="{ 'is-loading': loading }">
                    <span v-lang="'login-enterButton'"></span>
                </button>
            </form>
        </template>

        <template v-else>
            <p class="login-subtitle" v-lang="codeStepMessage"></p>

            <form @submit.prevent="login" class="login-form">
                <div class="input-group" :class="{ 'has-error': errors.length > 0 }">
                    <label>
                        <span v-lang="'login-codeLabel'"></span>
                        <span class="required-asterisk">*</span>
                    </label>
                    <div class="input-wrapper">
                        <span class="input-icon">
                            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                        </span>
                        <input
                            type="tel"
                            v-model="smsCode"
                            v-mask="'######'"
                            :disabled="loading"
                            :placeholder="t('login-codePlaceholder')"
                            @focus="scrollFormIntoView"
                        />
                    </div>
                    <span class="error-message" v-if="errors.length > 0">{{ errors[0] }}</span>
                </div>

                <div class="action-buttons-row">
                    <button type="button" class="btn-icon" @click="initRegularLoginFlow" :disabled="loading">
                        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
                    </button>

                    <button type="submit" class="btn-primary" :disabled="loading" :class="{ 'is-loading': loading }">
                        <span v-lang="'login-verifyButton'"></span>
                    </button>
                </div>

                <button
                    v-if="canResend"
                    type="button"
                    class="login-resend"
                    :disabled="resendDisabled"
                    @click="sendSMS"
                >
                    <span v-if="resendCountdown > 0">
                        {{ t("login-resendWait", { seconds: resendCountdown }) }}
                    </span>
                    <span v-else v-lang="'login-resendButton'"></span>
                </button>
            </form>
        </template>
    </AuthLayout>
</template>

<script>
import Api from "../../api";
import config from "../../config";
import { mapGetters, mapState, mapMutations } from "vuex";
import AuthLayout from "./auth-layout.vue";

const MAX_SMS_ATTEMPTS = 3;
const RESEND_WAIT_SECONDS = 180;

export default {
    components: {
        AuthLayout
    },
    data: () => ({
        step: "phone",
        loading: false,
        phone: "",
        smsCode: "",
        errors: [],
        smsClientId: null,
        smsSent: 0,
        resendCountdown: 0,
        resendTimer: null,
    }),
    beforeDestroy() {
        this.clearResendTimer();
    },
    mounted() {
        if (this.$route.params.code) {
            this.loading = true;
            Api.get("client/login/auth-code", {
                code: this.$route.params.code,
            })
                .then((response) => {
                    if (response.body.status === "success") {
                        this.errors = [];
                        this.setClient(response.body.client);
                        this.setToken(response.body.token);
                        if (this.clientHasProfileCompleted) {
                            this.$router.push("/dashboard");
                        } else {
                            this.$router.push("/profile");
                        }
                    }
                    if (response.body.status === "failed") {
                        this.initRegularLoginFlow();
                    }
                    this.loading = false;
                })
                .catch((response) => {
                    this.loading = false;
                    this.$router.push("/");
                    this.initRegularLoginFlow();
                });
        } else {
            this.initRegularLoginFlow();
        }
    },
    computed: {
        ...mapState({
            client: (state) => state.client,
            token: (state) => state.token,
        }),
        ...mapGetters([
            "clientHasProfileCompleted",
        ]),
        canResend() {
            return this.smsSent > 0 && this.smsSent < MAX_SMS_ATTEMPTS;
        },
        resendDisabled() {
            return this.loading || this.resendCountdown > 0;
        },
        codeStepMessage() {
            return this.smsSent > 1 ? "login-step2Message2" : "login-step2Message1";
        },
    },
    methods: {
        ...mapMutations(["setClient", "setToken"]),
        clearResendTimer() {
            if (this.resendTimer) {
                window.clearInterval(this.resendTimer);
                this.resendTimer = null;
            }
        },
        startResendCountdown() {
            this.clearResendTimer();
            this.resendCountdown = RESEND_WAIT_SECONDS;
            this.resendTimer = window.setInterval(() => {
                this.resendCountdown -= 1;
                if (this.resendCountdown <= 0) {
                    this.clearResendTimer();
                }
            }, 1000);
        },
        initRegularLoginFlow() {
            this.smsClientId = null;
            this.smsSent = 0;
            this.resendCountdown = 0;
            this.clearResendTimer();

            if (!config.isPreview && this.client.id && this.token) {
                if (this.clientHasProfileCompleted) {
                    this.$router.push("/dashboard");
                } else {
                    this.$router.push("/profile");
                }
            }
            this.step = "phone";
        },
        scrollFormIntoView(event) {
            const scrollTarget = () => {
                const form = event.target.closest(".login-form");
                const submitButton = form && form.querySelector(".btn-primary");

                if (submitButton) {
                    submitButton.scrollIntoView({ block: "end", behavior: "smooth" });
                }
            };

            this.$nextTick(scrollTarget);
            window.setTimeout(scrollTarget, 300);
        },
        sendSMS() {
            if (config.isPreview) {
                this.errors = [];
                this.smsClientId = "preview";
                this.smsCode = String(Math.floor(100000 + Math.random() * 900000));
                this.step = "code";
                this.smsSent += 1;
                this.startResendCountdown();

                return;
            }

            this.loading = true;
            Api.get("client/login/send-sms", {
                phone: this.phone,
                placeId: this.$store.state.place.id,
            })
                .then((response) => {
                    if (response.body.status === "success") {
                        this.errors = [];
                        this.smsClientId = response.body.client.id;
                        this.step = "code";
                        this.smsSent += 1;
                        this.startResendCountdown();
                    }
                    if (response.body.status === "failed") {
                        this.errors = [this.t("login-errorUnknown")];
                    }
                    this.loading = false;
                })
                .catch((response) => {
                    if (response && response.body && response.body.status === "Not Found") {
                        this.errors = [this.t("login-errorPhoneNotFound")];
                    } else {
                        this.errors = [this.t("login-errorUnknown")];
                    }
                    this.loading = false;
                });
        },
        login() {
            if (config.isPreview) {
                this.errors = [];
                this.$router.push("/profile").catch(() => {});

                return;
            }

            this.loading = true;
            Api.get("client/login/auth", {
                clientId: this.smsClientId,
                code: this.smsCode,
            })
                .then((response) => {
                    if (response.body.status === "success") {
                        this.errors = [];
                        this.setClient(response.body.client);
                        this.setToken(response.body.token);
                        if (this.clientHasProfileCompleted) {
                            this.$router.push("/dashboard");
                        } else {
                            this.$router.push("/profile");
                        }
                    }
                    if (response.body.status === "failed") {
                        this.errors = [this.t("login-errorInvalidCode")];
                    }
                    this.loading = false;
                })
                .catch((response) => {
                    this.errors = [this.t("login-errorUnknown")];
                    this.loading = false;
                });
        },
    },
};
</script>

